import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import Seo from "@/components/Seo";
import SectionHeading from "@/components/SectionHeading";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { fetchProducts, fetchShopifyProducts } from "@/lib/api";
import { CATEGORIES, OCCASION_FILTERS, STYLE_FILTERS, MATERIAL_FILTERS } from "@/config/site";

const Chip = ({ active, onClick, children, testId }) => (
    <button
        onClick={onClick}
        data-testid={testId}
        className={`text-[11px] tracking-[0.18em] uppercase px-4 py-2 border transition-colors duration-300 ${
            active ? "bg-gold text-obsidian border-gold" : "border-gold/25 text-sand hover:border-gold/60 hover:text-gold"
        }`}
    >
        {children}
    </button>
);

const FilterGroup = ({ title, children }) => (
    <div className="mb-8">
        <p className="eyebrow !text-[10px] mb-4">{title}</p>
        <div className="flex flex-wrap gap-2">{children}</div>
    </div>
);

const Catalogue = () => {
    const [params, setParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mobileFilters, setMobileFilters] = useState(false);
    const [q, setQ] = useState(params.get("q") || "");

    const filters = useMemo(
        () => ({
            collection: params.get("collection") || "",
            category: params.get("category") || "",
            occasion: params.get("occasion") || "",
            style: params.get("style") || "",
            material: params.get("material") || "",
            q: params.get("q") || "",
        }),
        [params]
    );

    useEffect(() => {
        setLoading(true);
        const p = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
        Promise.all([
            fetchShopifyProducts(p).catch(() => null),
            fetchProducts(p).catch(() => ({ items: [] })),
        ])
            .then(([shopify, local]) => {
                const shopifyItems = shopify?.items || [];
                setItems(shopifyItems.length ? shopifyItems : local.items);
            })
            .finally(() => setLoading(false));
    }, [filters]);

    const update = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value);
        else next.delete(key);
        if (key === "collection" && ["gold", "diamond", "bridal", "mens"].includes(value)) next.delete("category");
        setParams(next);
    };

    const submitSearch = (e) => {
        e.preventDefault();
        update("q", q.trim());
    };

    const filterPanel = (
        <div data-testid="catalogue-filters">
            <FilterGroup title="Category">
                {CATEGORIES.map((c) => (
                    <Chip
                        key={c.value || "all"}
                        testId={`filter-category-${c.value || "all"}`}
                        active={c.type === "collection" ? filters.collection === c.value : c.type === "category" ? filters.category === c.value : !filters.collection && !filters.category}
                        onClick={() => {
                            if (!c.value) {
                                const next = new URLSearchParams(params);
                                next.delete("collection");
                                next.delete("category");
                                setParams(next);
                            } else update(c.type === "collection" ? "collection" : "category", c.value);
                        }}
                    >
                        {c.label}
                    </Chip>
                ))}
            </FilterGroup>
            <FilterGroup title="Occasion">
                {OCCASION_FILTERS.map((o) => (
                    <Chip key={o} testId={`filter-occasion-${o}`} active={filters.occasion === o} onClick={() => update("occasion", filters.occasion === o ? "" : o)}>
                        {o}
                    </Chip>
                ))}
            </FilterGroup>
            <FilterGroup title="Style">
                {STYLE_FILTERS.map((s) => (
                    <Chip key={s} testId={`filter-style-${s}`} active={filters.style === s} onClick={() => update("style", filters.style === s ? "" : s)}>
                        {s}
                    </Chip>
                ))}
            </FilterGroup>
            <FilterGroup title="Material">
                {MATERIAL_FILTERS.map((m) => (
                    <Chip key={m} testId={`filter-material-${m.toLowerCase()}`} active={filters.material === m} onClick={() => update("material", filters.material === m ? "" : m)}>
                        {m}
                    </Chip>
                ))}
            </FilterGroup>
        </div>
    );

    return (
        <main className="pt-32 sm:pt-40 pb-24 min-h-screen">
            <Seo
                title="Collections — Gold, Diamond & Bridal Jewellery | BSK Jewellers Amritsar"
                siteName="BSK Jewellers"
                description="Browse the BSK Jewellers catalogue — gold, diamond, bridal and men's jewellery in Rani Ka Bagh, Amritsar. Filter by category, occasion, style and material."
                image="/images/flatlay.jpg"
            />
            <div className="max-w-[1500px] mx-auto px-5 sm:px-8">
                <SectionHeading
                    align="left"
                    eyebrow="The Catalogue"
                    title={<>Jewellery, <span className="italic gold-text">Curated</span></>}
                    copy="Every piece shown is available to view at our Amritsar showroom. Prices on request — enquire on WhatsApp for current details."
                />
                <form onSubmit={submitSearch} className="relative max-w-xl mb-12">
                    <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gold" strokeWidth={1.5} />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="catalogue-search-input"
                        placeholder="Search — gold ring, bridal necklace, diamond earrings…"
                        className="w-full bg-charcoal/60 border border-gold/20 focus:border-gold outline-none pl-11 pr-4 py-3.5 text-sm text-ivory placeholder:text-sand/50 transition-colors"
                    />
                </form>

                <button
                    onClick={() => setMobileFilters(!mobileFilters)}
                    data-testid="mobile-filters-toggle"
                    className="lg:hidden flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-gold border border-gold/40 px-5 py-3 mb-8"
                >
                    {mobileFilters ? <X size={15} /> : <SlidersHorizontal size={15} />} Filters
                </button>
                {mobileFilters && <div className="lg:hidden mb-10">{filterPanel}</div>}

                <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-12">
                    <aside className="hidden lg:block">{filterPanel}</aside>
                    <div>
                        <p className="text-xs tracking-[0.2em] uppercase text-sand mb-6" data-testid="catalogue-count">
                            {loading ? "Curating…" : `${items.length} piece${items.length === 1 ? "" : "s"}`}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="catalogue-grid">
                            {items.map((p, i) => (
                                <Reveal key={p.slug} delay={(i % 3) * 0.06}>
                                    <ProductCard product={p} />
                                </Reveal>
                            ))}
                        </div>
                        {!loading && items.length === 0 && (
                            <p className="text-sand font-serif italic text-2xl mt-10" data-testid="catalogue-empty">
                                No pieces match — try clearing a filter, or ask us on WhatsApp.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
};

export default Catalogue;
